import React, { useRef, useState, useEffect } from 'react';

/**
 * PinInput Component
 * A modern 6-digit PIN input with individual boxes for each digit
 */
const PinInput = ({ value = '', onChange, disabled = false, autoFocus = true }) => {
  const [pins, setPins] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  // Initialize refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Update pins when value changes externally
  useEffect(() => {
    if (value && value.length <= 6) {
      const newPins = value.split('');
      while (newPins.length < 6) {
        newPins.push('');
      }
      setPins(newPins);
    }
  }, [value]);

  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, e) => {
    const val = e.target.value;

    // Only allow digits
    if (val && !/^\d$/.test(val)) {
      return;
    }

    const newPins = [...pins];
    newPins[index] = val;
    setPins(newPins);

    // Call onChange with complete value
    const completeValue = newPins.join('');
    if (onChange) {
      onChange(completeValue);
    }

    // Auto-focus next input
    if (val && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newPins = [...pins];

      if (pins[index]) {
        // Clear current box
        newPins[index] = '';
        setPins(newPins);
        if (onChange) {
          onChange(newPins.join(''));
        }
      } else if (index > 0) {
        // Move to previous box and clear it
        newPins[index - 1] = '';
        setPins(newPins);
        inputRefs.current[index - 1]?.focus();
        if (onChange) {
          onChange(newPins.join(''));
        }
      }
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      // Let the paste event handle it
      return;
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    // Only allow digits
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits) {
      const newPins = digits.split('');
      while (newPins.length < 6) {
        newPins.push('');
      }
      setPins(newPins);

      if (onChange) {
        onChange(digits);
      }

      // Focus the next empty box or last box
      const nextIndex = Math.min(digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleFocus = (index) => {
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {pins.map((pin, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={pin}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={`
            w-12 h-14 sm:w-14 sm:h-16
            text-2xl sm:text-3xl font-bold text-center
            border-2 rounded-lg
            transition-all duration-200
            ${pin
              ? 'border-green-500 bg-green-50'
              : 'border-gray-300 bg-white'
            }
            ${disabled
              ? 'bg-gray-100 cursor-not-allowed'
              : 'hover:border-primary-400'
            }
            focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500
            ${index === 2 ? 'mr-2 sm:mr-4' : ''}
          `}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
};

export default PinInput;